// app/src/lib/ocr/bulkUnits.js — remembered "sold in bulk" units of measure.
//
// Catch-weight items (a sack of onions, a case of eggs) are billed by actual
// weight, so a line's printed total won't equal its container count × the
// per-pound price. Rather than make the operator re-confirm that on every
// receipt, we LEARN the units they mark as bulk — almost always a container
// UOM like CS (case), SX (sack), BAG, BX — and default future lines with the
// same unit to bulk. The association lives on userProfile.bulkUoms so it rides
// along in the encrypted snapshot backup (userProfile is allow-listed there).
//
// Same user-driven, no-built-in-defaults model as the cashflow keyword lists
// (see cashflow/rent.js): nothing is bulk until the operator says so once.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';

const MAX_UNITS = 40;

/** Canonical form of a unit of measure: trimmed, upper-cased, trailing dots
 *  dropped so "cs", "CS" and "Cs." all collapse to "CS". Empty → ''. */
export function normalizeUom(uom) {
  return String(uom || '').trim().toUpperCase().replace(/\.+$/, '');
}

/** Is this line's unit one the operator has marked as sold-in-bulk? */
export function isBulkUom(uom, units) {
  const key = normalizeUom(uom);
  if (!key || !Array.isArray(units)) return false;
  return units.includes(key);
}

export async function getBulkUoms() {
  const c = await currentClaims();
  if (!c) return [];
  const p = await db.userProfile.get(c.sub);
  return (p && Array.isArray(p.bulkUoms)) ? p.bulkUoms : [];
}

/** Persist the remembered set verbatim (normalized + de-duped). */
export async function setBulkUoms(list) {
  const c = await currentClaims();
  if (!c) throw new Error('bulkUnits: not signed in');
  const clean = [...new Set((list || []).map(normalizeUom).filter(Boolean))].slice(0, MAX_UNITS);
  const existing = (await db.userProfile.get(c.sub)) || { userId: c.sub };
  await db.userProfile.put({ ...existing, userId: c.sub, bulkUoms: clean });
  return clean;
}

/** Remember `uom` as a bulk unit. No-op for a blank/unknown unit. */
export async function addBulkUom(uom) {
  const key = normalizeUom(uom);
  if (!key) return getBulkUoms();
  const cur = await getBulkUoms();
  if (cur.includes(key)) return cur;
  return setBulkUoms([...cur, key]);
}

/** Stop treating `uom` as a bulk unit. */
export async function removeBulkUom(uom) {
  const key = normalizeUom(uom);
  if (!key) return getBulkUoms();
  const cur = await getBulkUoms();
  if (!cur.includes(key)) return cur;
  return setBulkUoms(cur.filter((u) => u !== key));
}
